/**
 * Maps an expense `category` (and optional `subcategory`) to the
 * lucide icon name that represents it.
 *
 * Both web (`apps/web/src/utils/expenseCategoryIcons.tsx`) and mobile
 * (`apps/mobile/src/utils/expenseCategoryIcons.ts`) re-export the same
 * mapping resolved against their own platform's lucide package.
 *
 * Subcategory-level dispatch lives next door in
 * `expense-subcategory-icons.ts` — `getExpenseIconName` consults it
 * first when a subcategory is supplied, falling back to the
 * category-level mapping below otherwise. The default for unknown
 * values is `CircleDot`.
 */
import { getExpenseSubcategoryIconName } from './expense-subcategory-icons';

/** Lucide PascalCase component names used by expense categories. */
export type ExpenseCategoryIconName =
  | 'Bed'
  | 'Car'
  | 'CircleDot'
  | 'CupSoda'
  | 'Gamepad'
  | 'GraduationCap'
  | 'HeartPulse'
  | 'Home'
  | 'ShoppingBag'
  | 'Utensils';

export const EXPENSE_CATEGORY_ICON_NAMES: Record<
  string,
  ExpenseCategoryIconName
> = {
  '': 'CircleDot',
  food: 'Utensils',
  drinks: 'CupSoda',
  transport: 'Car',
  accommodation: 'Bed',
  entertainment: 'Gamepad',
  shopping: 'ShoppingBag',
  utilities: 'Home',
  healthcare: 'HeartPulse',
  education: 'GraduationCap',
  other: 'CircleDot',
};

export const DEFAULT_EXPENSE_CATEGORY_ICON_NAME: ExpenseCategoryIconName =
  'CircleDot';

export function getExpenseCategoryIconName(
  category: string | undefined
): ExpenseCategoryIconName {
  if (!category) return DEFAULT_EXPENSE_CATEGORY_ICON_NAME;
  return (
    EXPENSE_CATEGORY_ICON_NAMES[category] ?? DEFAULT_EXPENSE_CATEGORY_ICON_NAME
  );
}

/**
 * Combined resolver: tries the subcategory-specific icon first
 * (`food/pizza` → `Pizza`, `transport/flight` → `Plane`, etc.) and
 * falls back to the broad category icon when there's no match.
 *
 * Returns a string rather than the narrow `ExpenseCategoryIconName`
 * union because subcategory icons can be any lucide export — far
 * more than the 10 used by category-level mapping.
 */
export function getExpenseIconName(
  category: string | undefined,
  subcategory?: string | null
): string {
  const sub = getExpenseSubcategoryIconName(category, subcategory);
  if (sub) return sub;
  return getExpenseCategoryIconName(category);
}
